import { mountPortalApp, type PortalAppTile } from '@yuksalish/ui'
import {
  Banknote,
  BarChart3,
  CalendarClock,
  ClipboardCheck,
  Factory,
  HardHat,
  HeartHandshake,
  PackageSearch,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Store,
  UserRoundCog,
  Users,
} from 'lucide-vue-next'

type PortalUrlConfig = {
  apps?: Record<string, string>
}

const defaultAppBase = String(import.meta.env.VITE_FRONTEND_APP_BASE_URL ?? '').replace(/\/$/, '')

function fallbackAppUrl(path: string): string {
  return `${defaultAppBase}/${path}`
}

async function loadPortalUrlConfig(): Promise<Record<string, string>> {
  try {
    const response = await fetch('/portal-app-urls.json', {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })

    if (!response.ok) {
      return {}
    }

    const config = (await response.json()) as PortalUrlConfig
    return config.apps ?? {}
  } catch {
    return {}
  }
}

function applyRuntimeUrls(
  apps: PortalAppTile[],
  urls: Record<string, string>,
): PortalAppTile[] {
  return apps.map((app) => ({
    ...app,
    href: urls[app.code] || app.href,
  }))
}

const apps: PortalAppTile[] = [
  {
    code: 'admin',
    title: { uz: 'Maʼmuriyat', ru: 'Администрирование', en: 'Administration' },
    description: {
      uz: 'Foydalanuvchilar, lavozimlar, ruxsatlar, maʼlumotnomalar, bildirishnomalar, audit va tizim sozlamalari.',
      ru: 'Пользователи, должности, права, справочники, уведомления, аудит и системные настройки.',
      en: 'Users, positions, permissions, reference data, notifications, audit, and system settings.',
    },
    href: fallbackAppUrl('admin/'),
    accent: '#27705d',
    requiredPermissions: ['users.manage'],
    icon: ShieldCheck,
  },
  {
    code: 'engineering',
    title: { uz: 'Muhandislik', ru: 'Инженерия', en: 'Engineering' },
    description: {
      uz: 'Mahsulotlar, xomashyo, BOM, texnik pasportlar, ishlab chiqarish buyurtmalari, lotlar va tannarx.',
      ru: 'Продукты, сырьё, BOM, технические паспорта, производственные заказы, партии и себестоимость.',
      en: 'Products, raw materials, BOM, technical passports, production orders, lots, and costing.',
    },
    href: fallbackAppUrl('engineering/'),
    accent: '#3568a8',
    requiredPermissions: ['products.view'],
    icon: Settings,
  },
  {
    code: 'shopfloor',
    title: { uz: 'Sex', ru: 'Цех', en: 'Shopfloor' },
    description: {
      uz: 'Smena paneli, faol buyurtmalar, topshiriqlar, soatlik ishlab chiqarish, to‘xtashlar va smena topshirish.',
      ru: 'Панель смены, активные заказы, задания, почасовой выпуск, простои и передача смены.',
      en: 'Shift dashboard, active orders, assignments, hourly output, downtime, and handover.',
    },
    href: fallbackAppUrl('shopfloor/'),
    accent: '#0f766e',
    requiredPermissions: ['production_orders.view'],
    icon: Factory,
  },
  {
    code: 'worker-kiosk',
    title: { uz: 'Ishchi kioski', ru: 'Киоск работника', en: 'Worker Kiosk' },
    description: {
      uz: 'Ishchi kirishi, lot tanlash, dona hisoblash, yaroqsizlar va to‘xtashlar uchun sensor terminal.',
      ru: 'Сенсорный терминал для входа работника, выбора партии, учёта штук, брака и простоев.',
      en: 'Touch terminal for worker login, lot selection, piece counts, rejects, and downtime.',
    },
    href: fallbackAppUrl('worker-kiosk/'),
    accent: '#047857',
    requiredPermissions: ['production_orders.view'],
    icon: HardHat,
  },
  {
    code: 'machine-operator',
    title: { uz: 'Uskuna operatori', ru: 'Оператор станка', en: 'Machine Operator' },
    description: {
      uz: 'Uskuna topshiriqlari, sozlash parametrlari, hisoblagichlar, nosozliklar, to‘xtashlar va taʼmir so‘rovlari.',
      ru: 'Назначения станков, параметры настройки, счётчики, неисправности, простои и заявки на обслуживание.',
      en: 'Machine assignments, setup parameters, counters, faults, downtime, and maintenance requests.',
    },
    href: fallbackAppUrl('machine-operator/'),
    accent: '#1d4ed8',
    requiredPermissions: ['production_orders.view'],
    icon: UserRoundCog,
  },
  {
    code: 'warehouse',
    title: { uz: 'Ombor', ru: 'Склад', en: 'Warehouse' },
    description: {
      uz: 'Qabul qilish, inventar, berish, o‘tkazmalar, tuzatishlar, yig‘ish, qadoqlash, jo‘natish va yorliqlar.',
      ru: 'Приёмка, запасы, выдача, перемещения, корректировки, подбор, упаковка, отгрузка и этикетки.',
      en: 'Receiving, inventory, issues, transfers, adjustments, picking, packing, shipping, and labels.',
    },
    href: fallbackAppUrl('warehouse/'),
    accent: '#8b5e34',
    requiredPermissions: ['inventory.view'],
    icon: PackageSearch,
  },
  {
    code: 'planning',
    title: { uz: 'Rejalashtirish', ru: 'Планирование', en: 'Planning' },
    description: {
      uz: 'Jadval doskasi, ishlab chiqarish kalendari, quvvat, material mavjudligi va buyurtmalarni chiqarish.',
      ru: 'Доска расписания, производственный календарь, мощности, наличие материалов и выпуск заказов.',
      en: 'Schedule board, production calendar, capacity, material availability, and releases.',
    },
    href: fallbackAppUrl('planning/'),
    accent: '#896318',
    requiredPermissions: ['production_orders.view'],
    icon: CalendarClock,
  },
  {
    code: 'quality',
    title: { uz: 'Sifat', ru: 'Качество', en: 'Quality' },
    description: {
      uz: 'Kirish, jarayon va yakuniy nazorat; nuqsonlar, bloklash, qayta ishlash va chiqarish.',
      ru: 'Входной, операционный и финальный контроль; дефекты, удержания, доработка и выпуск.',
      en: 'Incoming, in-process, and final inspection with defects, holds, rework, and release.',
    },
    href: fallbackAppUrl('quality/'),
    accent: '#8a4b75',
    requiredPermissions: ['quality.view'],
    icon: ClipboardCheck,
  },
  {
    code: 'procurement',
    title: { uz: 'Xaridlar', ru: 'Закупки', en: 'Procurement' },
    description: {
      uz: 'Xarid so‘rovlari, tasdiqlar, RFQ, takliflar, buyurtmalar, yetkazib berish kuzatuvi va solishtirish.',
      ru: 'Заявки на закупку, согласования, RFQ, предложения, заказы, отслеживание поставок и сверка.',
      en: 'Purchase requests, approvals, RFQs, quotations, orders, delivery tracking, and reconciliation.',
    },
    href: fallbackAppUrl('procurement/'),
    accent: '#9a5b13',
    requiredPermissions: ['purchase_orders.view'],
    icon: ShoppingCart,
  },
  {
    code: 'sales',
    title: { uz: 'Sotuv', ru: 'Продажи', en: 'Sales' },
    description: {
      uz: 'Mijozlar, narxlar, tijorat takliflari, sotuv buyurtmalari, mavjudlik, yetkazish, qaytarish va marja.',
      ru: 'Клиенты, прайс-листы, предложения, заказы продаж, наличие, доставка, возвраты и маржа.',
      en: 'Customers, pricelists, quotations, sales orders, availability, delivery, returns, and margin preview.',
    },
    href: fallbackAppUrl('sales/'),
    accent: '#be123c',
    requiredPermissions: ['orders.view'],
    icon: Store,
  },
  {
    code: 'finance',
    title: { uz: 'Moliya', ru: 'Финансы', en: 'Finance' },
    description: {
      uz: 'Debitorlik, kreditorlik, kassa, bank, ish haqi, bosh kitob, jurnallar, soliqlar, hisobotlar va tannarx.',
      ru: 'Дебиторка, кредиторка, касса, банк, зарплата, главная книга, журналы, налоги, отчёты и себестоимость.',
      en: 'Receivables, payables, cash, bank, payroll, ledger, journals, taxes, reports, and costing.',
    },
    href: fallbackAppUrl('finance/'),
    accent: '#166534',
    requiredPermissions: ['finance_reports.view'],
    icon: Banknote,
  },
  {
    code: 'hr',
    title: { uz: 'HR', ru: 'HR', en: 'HR' },
    description: {
      uz: 'Xodimlar, davomat, taʼtil, qo‘shimcha ish, o‘qitish va malakalar.',
      ru: 'Сотрудники, посещаемость, отпуска, сверхурочные, обучение и квалификации.',
      en: 'Employees, attendance, leave, overtime, training, and qualifications.',
    },
    href: fallbackAppUrl('hr/'),
    accent: '#7c3aed',
    requiredPermissions: ['employees.view'],
    icon: Users,
  },
  {
    code: 'management',
    title: { uz: 'Boshqaruv', ru: 'Руководство', en: 'Management' },
    description: {
      uz: 'Rahbar paneli, reja va fakt, samaradorlik, tor joylar, xarajatlar, prognozlar va ogohlantirishlar.',
      ru: 'Обзор руководителя, план-факт, производительность, узкие места, затраты, прогнозы и оповещения.',
      en: 'Executive overview, plan versus actual, productivity, bottlenecks, costs, forecasts, and alerts.',
    },
    href: fallbackAppUrl('management/'),
    accent: '#0e7490',
    requiredPermissions: ['management.view'],
    icon: BarChart3,
  },
  {
    code: 'maintenance',
    title: { uz: 'Texnik xizmat', ru: 'Обслуживание', en: 'Maintenance' },
    description: {
      uz: 'Uskunalar, profilaktika jadvallari, ish buyurtmalari, taʼmirlar, hisoblagichlar, nosozliklar va MTBF/MTTR.',
      ru: 'Оборудование, профилактические графики, наряды, ремонты, счётчики, неисправности и MTBF/MTTR.',
      en: 'Equipment, preventive schedules, work orders, repairs, meters, faults, requests, and MTBF/MTTR.',
    },
    href: fallbackAppUrl('maintenance/'),
    accent: '#52525b',
    requiredPermissions: ['maintenance.view'],
    icon: HeartHandshake,
  },
]

void (async () => {
const runtimeUrls = await loadPortalUrlConfig()

mountPortalApp({
  title: {
    uz: 'Ilovalar portali',
    ru: 'Портал приложений',
    en: 'Apps Portal',
  },
  description: {
    uz: 'Bir marta tizimga kiring va hisobingizga ruxsat berilgan ERP ilovalarini oching. Ruxsat bo‘lmagan ilovalar ko‘rinadi, lekin o‘chirilgan holatda qoladi.',
    ru: 'Войдите один раз и откройте ERP-приложения, доступные вашей учётной записи. Приложения без нужных прав остаются видимыми, но отключёнными.',
    en: 'Sign in once, then open the ERP applications available to your account. Apps without the required permissions stay visible but disabled.',
  },
  apps: applyRuntimeUrls(apps, runtimeUrls),
})
})()
